import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HomeIcon from '@mui/icons-material/Home';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Capacitor } from '@capacitor/core';
import AuthStatus from './AuthStatus';
import UserInfoBox from './UserInfoBox';
import { useUserRole } from './UserRoleProvider';
import { getPageItemBySlug } from '../services/contentful';
import './Navigation.css';

interface NavigationProps {
  isOpen: boolean;
  onClose: () => void;
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

type DropdownKey = 'agency' | 'corporate-connect' | 'accounting' | 'marketing' | 'technology' | 'services' | null;

const Navigation: React.FC<NavigationProps> = ({ isOpen, onClose }) => {
  const { hasRole, isNewGrad, isOnboarding, canUseSelfService, canViewAgencyStatements } = useUserRole();
  const [openDropdowns, setOpenDropdowns] = useState<Set<DropdownKey>>(new Set());
  const [hasRingCentralPage, setHasRingCentralPage] = useState(false);
  // Read the path from the router, not window.location. Now that these links navigate
  // client-side the component no longer remounts per navigation, so the active state has to
  // come from a reactive source to update at all.
  const currentPath = useLocation().pathname;

  const isActive = (href: string) => {
    if (href === '/') return currentPath === '/';
    return currentPath.startsWith(href);
  };

  useEffect(() => {
    const path = currentPath;
    const open = new Set<DropdownKey>();
    // Agency Management expanded by default on desktop. On mobile (the footer-nav breakpoint) it starts
    // collapsed to keep the drawer short, unless the page is one of its own.
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile || path.startsWith('/agency-management') || path === '/my-tickets') open.add('agency');
    if (path.startsWith('/accounting') || path.startsWith('/make-payment')) {
      open.add('corporate-connect');
      open.add('accounting');
    }
    if (path.startsWith('/marketing')) {
      open.add('corporate-connect');
      open.add('marketing');
    }
    if (path.startsWith('/technology')) {
      open.add('corporate-connect');
      open.add('technology');
    }
    if (path.startsWith('/services')) {
      open.add('corporate-connect');
      open.add('services');
    }
    if (path === '/key-contacts') {
      open.add('corporate-connect');
    }
    setOpenDropdowns(open);
  }, []);

  // Only show the RingCentral tech tool once its Contentful entry is published.
  useEffect(() => {
    let cancelled = false;
    getPageItemBySlug('ringcentral')
      .then((entry) => {
        if (!cancelled) setHasRingCentralPage(Boolean(entry));
      })
      .catch(() => {
        if (!cancelled) setHasRingCentralPage(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleDropdown = (key: DropdownKey) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isDropdownOpen = (key: DropdownKey) => openDropdowns.has(key);

  const handleLinkClick = () => {
    onClose();
  };

  const agencyMgmtItems = [
    { name: 'My Agency', url: '/agency-management/my-agency' },
    { name: 'Support Portal', url: '/my-tickets' },
    ...(canViewAgencyStatements ? [{ name: 'Agency Statements', url: '/agency-management/statements' }] : []),
    { name: 'Agency Operations', url: '/agency-management/operations' },
    ...(!hasRole('agent-corporate') ? [{ name: 'Order Equipment', url: '/agency-management/equipment' }] : []),
  ];

  const accountingItems = [
    { name: 'Accounting', url: '/accounting' },
    { name: 'Make a Payment', url: '/make-payment' },
  ];

  const marketingItems: { name: string; url: string; external?: boolean }[] = [
    { name: 'Branding', url: '/marketing/branding' },
    { name: 'Canva', url: '/marketing/canva' },
    { name: 'Marketing Resources', url: '/marketing/resources' },
    { name: 'We Insure Company Store', url: 'https://weinsure.axomo.com/', external: true },
  ];

  const techTools: { name: string; url: string; external?: boolean }[] = [
    { name: '1password', url: '/technology/1password' },
    { name: 'Applied Epic', url: '/technology/applied-epic' },
    { name: 'EZLynx', url: '/technology/ezlynx' },
    { name: 'InsuredMine', url: '/technology/insuredmine' },
    { name: 'Microsoft 365', url: 'https://outlook.office.com/mail/', external: true },
    { name: 'QuoteRush', url: '/technology/quoterush' },
    ...(hasRingCentralPage ? [{ name: 'RingCentral', url: '/technology/ringcentral' }] : []),
  ];

  const serviceRequests: { name: string; url: string }[] = [
    { name: 'Mortgage/Lienholder Documents', url: '/services/mortgage-lienholder-request' },
    { name: 'Commercial Certificate of Insurance', url: '/services/commercial-certificate-request' },
  ];

  const renderDropdown = (
    key: DropdownKey,
    label: React.ReactNode,
    items: { name: string; url: string; external?: boolean }[],
    parentUrl?: string,
  ) => (
    <li className={`dropdown ${isDropdownOpen(key) ? 'active' : ''}`}>
      <a
        href={parentUrl || '#'}
        onClick={(e) => {
          if (!parentUrl || parentUrl === '#') e.preventDefault();
          toggleDropdown(key);
        }}
      >
        {label}
        <span className="dropdown-arrow">{isDropdownOpen(key) ? <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> : <ChevronRightIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />}</span>
      </a>
      {isDropdownOpen(key) && (
        <ul className="dropdown-menu">
          {items.map((item) => (
            <li key={item.name}>
              <a
                href={item.url}
                className={!item.external && isActive(item.url) ? 'active' : ''}
                target={item.external ? '_blank' : '_self'}
                rel={item.external ? 'noopener noreferrer' : undefined}
                onClick={handleLinkClick}
              >
                {item.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  return (
    <nav className={`sidebar-navigation ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <UserInfoBox />
      </div>

      <ul className="sidebar-links">
        {isNewGrad && <li><a href="/new-grad-roadmap" className={isActive('/new-grad-roadmap') ? 'active' : ''} onClick={handleLinkClick}><SchoolIcon fontSize="small" /> My Roadmap</a></li>}
        {isOnboarding && <li><a href="/my-onboarding" className={isActive('/my-onboarding') ? 'active' : ''} onClick={handleLinkClick}><AssignmentTurnedInIcon fontSize="small" /> My Onboarding</a></li>}
        <li><a href="/" className={isActive('/') ? 'active' : ''} onClick={handleLinkClick}><HomeIcon fontSize="small" /> Home</a></li>
        <li className="hide-on-mobile"><a href="/assistant" className={isActive('/assistant') ? 'active' : ''} onClick={handleLinkClick}><AutoAwesomeIcon fontSize="small" /> Ask AgencyOS</a></li>
        <li className="hide-on-mobile"><a href="/calendar" className={isActive('/calendar') ? 'active' : ''} onClick={handleLinkClick}><CalendarMonthIcon fontSize="small" /> Calendar</a></li>
        {renderDropdown('agency', <><BusinessIcon fontSize="small" /> Agency Management</>, agencyMgmtItems, '/agency-management/my-agency')}
        <li><a href="/agent-directory" className={isActive('/agent-directory') ? 'active' : ''} onClick={handleLinkClick}><PlaceIcon fontSize="small" /> Agent Directory</a></li>
        <li><a href="/carrier-search" className={isActive('/carrier') ? 'active' : ''} onClick={handleLinkClick}><SearchIcon fontSize="small" /> Carrier Search</a></li>
        {/* Shown only to users granted self-service, matching the route's gate; the API denies the rest
            either way. */}
        {canUseSelfService && <li><a href="/self-service" className={currentPath === '/self-service' || currentPath.startsWith('/self-service/insureds') ? 'active' : ''} onClick={handleLinkClick}><PersonSearchIcon fontSize="small" /> Self-service</a></li>}
        {/* The reader's own Applied Epic queue, behind the same grant as self-service. */}
        {canUseSelfService && <li><a href="/self-service/activities" className={isActive('/self-service/activities') ? 'active' : ''} onClick={handleLinkClick}><AssignmentIcon fontSize="small" /> My Activities</a></li>}
        {/* Agent Tools — temporarily hidden during dark deploy. Route still works at /agent-tools for direct navigation. */}
        {/* <li><a href="/agent-tools" className={isActive('/agent-tools') ? 'active' : ''} onClick={handleLinkClick}>🛠️ Agent Tools</a></li> */}
        <li className={`dropdown ${isDropdownOpen('corporate-connect') ? 'active' : ''}`}>
          <a href="#" onClick={(e) => { e.preventDefault(); toggleDropdown('corporate-connect'); }}>
            <HandshakeIcon fontSize="small" /> Corporate Connect
            <span className="dropdown-arrow">{isDropdownOpen('corporate-connect') ? <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> : <ChevronRightIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />}</span>
          </a>
          {isDropdownOpen('corporate-connect') && (
            <ul className="dropdown-menu">
              {renderDropdown('accounting', 'Accounting', accountingItems)}
              {renderDropdown('services', 'Service Requests', serviceRequests)}
              <li>
                <a href="/key-contacts" className={isActive('/key-contacts') ? 'active' : ''} onClick={handleLinkClick}>
                  Key Contacts
                </a>
              </li>
              {renderDropdown('marketing', 'Marketing', marketingItems)}
              {renderDropdown('technology', 'Technology', techTools)}
            </ul>
          )}
        </li>
        {/* Native only: push notifications don't exist in the browser build, and the page has
            nothing to offer there. */}
        {Capacitor.isNativePlatform() && <li><a href="/settings/notifications" className={isActive('/settings/notifications') ? 'active' : ''} onClick={handleLinkClick}><NotificationsIcon fontSize="small" /> Notification Settings</a></li>}
      </ul>

      <AuthStatus />
    </nav>
  );
};

export default Navigation;
